import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Flex, HomeLoader, Text } from '@opendoor/bricks/core';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { useLocation } from '@remix-run/react';
import { useMachine } from '@xstate/react';

import { ListingTag } from '__generated__/globalTypes';

import { Modal } from 'platform/bricks/Modal';
import navigationHelpers from 'platform/navigationHelpers';

import { trackStateUpdateNavigationEvent } from 'shared/helpers/hooks/useTrackPerformance';
import { NotFoundPage } from 'shared/routes/errors/NotFoundPage';

import { BackupOffer } from 'buying/components/buyDirectCheckout/BackupOffer';
import { Confirmation } from 'buying/components/buyDirectCheckout/Confirmation';
import { ContactDetails } from 'buying/components/buyDirectCheckout/ContactDetails';
import { ContingentSaleStep } from 'buying/components/buyDirectCheckout/ContigentSaleStep';
import { ContractDetails } from 'buying/components/buyDirectCheckout/ContractDetails';
import { FundingSource } from 'buying/components/buyDirectCheckout/FundingSource';
import { InfoPanel } from 'buying/components/buyDirectCheckout/InfoPanel';
import { OwnAgent } from 'buying/components/buyDirectCheckout/OwnAgent';
import { PreApproval } from 'buying/components/buyDirectCheckout/PreApproval';
import { ReviewDetails } from 'buying/components/buyDirectCheckout/ReviewDetails';
import { useBuyDirect } from 'buying/graphql/useBuyDirect';
import {
  BuyDirectCheckoutFlowStateMachine,
  CheckoutState,
} from 'buying/stateMachines/buyDirectCheckoutFlowStateMachine';

const BUY_DIRECT_DISCOUNT_FALLBACK = 1; // don't discount if missing
const ALLOW_DUPLICATE_OFFER_STATES = ['canceled', 'buyer_walked', 'rejected'];

const InterstitialLoader = React.memo(() => (
  <Flex flexDirection="column" mt={10} pt={10} alignItems="center">
    <HomeLoader />
  </Flex>
));

InterstitialLoader.displayName = 'InterstitialLoader';

/**
 * Build the redirect URL for the offer closing dashboard
 */
const redirectToClosingDashboard = (listingOfferId: string, contractSignatureId?: string) => {
  const url = new URL(window.location.href);
  url.pathname = `/dashboard/buy/offer/${listingOfferId}`;
  url.searchParams.set('bd', 'true');
  // Not always guaranteed if there is a failure with HelloSign downstream
  if (contractSignatureId) {
    url.searchParams.set('csi', contractSignatureId);
  }
  navigationHelpers.hardNavAssign(url);
};

/**
 * Buy Direct checkout flow
 */
export const BuyDirectCheckout: React.FC = () => {
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDuplicateOffersModal, setShowDuplicateOffersModal] = useState(false);
  const addressId = queryParams.get('id');
  // Query params for testing
  const isInContractOverride = queryParams.get('isInContract') === 'true';
  const hasMultipleOffersOverride = queryParams.get('hasMultipleOffers') === 'true';
  const hasVerbalOfferOverride = queryParams.get('hasVerbalAgreement') === 'true';

  const { customer, property, resaleOffers, loading, error } = useBuyDirect(addressId);

  const propertyTags = property?.listingMetadata?.tags;
  const isOpendoorOwned = property?.opendoorOwned;
  const photo = property?.photos?.edges?.[0]?.node?.urls?.fullSize ?? '';

  // @ts-expect-error -- variable is possibly undefined/null
  const listPrice = property?.listPrice / 100 ?? 0;
  const bedrooms = property?.bedrooms ?? 0;
  // @ts-expect-error -- variable is possibly undefined/null
  const bathrooms = property?.bathroomsFull + property?.bathroomsHalf ?? 0;
  const sqft = property?.sqFtTotalLiving ?? property?.sqFtAboveGrade;
  const street = property?.street ?? '';
  const city = property?.city ?? '';
  const stateAbbrv = property?.state ?? '';
  const postalCode = property?.postalCode ?? '';
  const displaySlug = property?.displaySlug ?? '';
  const sellerDisclosuresDownloadUrl = property?.sellerDisclosuresDownloadUrl ?? '';
  const buyDirectDiscount =
    // @ts-expect-error -- variable is possibly undefined/null
    1 - property?.listingMetadata?.buyDirectDiscountPercent ?? BUY_DIRECT_DISCOUNT_FALLBACK;
  const existingOffer = useMemo(
    () => resaleOffers?.find((offer) => offer?.address?.uuid === addressId),
    [addressId, resaleOffers],
  );

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const [state, send] = useMachine(BuyDirectCheckoutFlowStateMachine);

  // when the state changes - we want to track a navigation event
  useEffect(() => {
    trackStateUpdateNavigationEvent();
  }, [state.value]);

  useEffect(() => {
    if (state.value === CheckoutState.Idle && !loading && !error) {
      send({ type: 'setAddressId', value: addressId });
      send({
        type: 'setIsBuyDirectEligible',
        // @ts-expect-error -- variable is possibly undefined/null
        value: propertyTags.includes(ListingTag.BUY_DIRECT_ELIGIBLE),
      });
      send({
        type: 'setIsInContract',
        // @ts-expect-error -- variable is possibly undefined/null
        value: propertyTags.includes(ListingTag.BUY_DIRECT_SALE_PENDING) || isInContractOverride,
      });
      send({
        type: 'setHasMultipleOffers',
        value:
          // @ts-expect-error -- variable is possibly undefined/null
          propertyTags.includes(ListingTag.BUY_DIRECT_MULTIPLE_OFFERS) || hasMultipleOffersOverride,
      });
      send({
        type: 'setHasVerbalAgreement',
        value:
          // @ts-expect-error -- variable is possibly undefined/null
          propertyTags.includes(ListingTag.BUY_DIRECT_VERBAL_AGREEMENT) || hasVerbalOfferOverride,
      });
      send('FORWARD');
    }
  }, [
    addressId,
    send,
    state,
    isInContractOverride,
    hasMultipleOffersOverride,
    hasVerbalOfferOverride,
    propertyTags,
    loading,
    error,
  ]);

  // Only prevent customers from creating a duplicate contract if the existing offer is not canceled
  useEffect(() => {
    if (
      existingOffer &&
      existingOffer?.details?.state &&
      !ALLOW_DUPLICATE_OFFER_STATES.includes(existingOffer?.details?.state)
    ) {
      setShowDuplicateOffersModal(true);
    }
  }, [existingOffer]);

  // Show error toast if the offer cannot be created
  useEffect(() => {
    if (state?.context?.errorMessage && !state?.context?.listingOfferId) {
      setShowErrorModal(true);
    }
  }, [state?.context?.errorMessage, state?.context?.listingOfferId]);

  const closeDuplicateOffersModal = () => {
    redirectToClosingDashboard(
      String(existingOffer?.id),
      existingOffer?.details?.contractSignatureId,
    );
  };

  const fhaEligibilityDate = useMemo(() => {
    if (property?.fhaEligibleDate) {
      const { year, month, day } = property.fhaEligibleDate;
      return new Date(year, month - 1, day);
    }
    return undefined;
  }, [property?.fhaEligibleDate]);

  const handleCheckoutCompletionRedirect = () => {
    if (state.context.listingOfferId) {
      redirectToClosingDashboard(state.context.listingOfferId, state.context.contractSignatureId);
    }
  };

  // Pre-populate contact details if available on UserSession to avoid
  // creating a duplicate human and avoid fat fingered email input which
  // could create a new human and require a manual merge eventually
  const prePopulateContactDetails = useCallback(() => {
    if (state.context.contactDetails === undefined && customer) {
      const id = '0';
      send({
        type: 'setContactDetails',
        value: {
          [id]: {
            id,
            name: customer?.fullName,
            email: customer?.email,
            emailVerification: customer?.email,
          },
        },
      });
    }
  }, [customer, send, state.context.contactDetails]);

  useEffect(() => {
    if (!loading) {
      prePopulateContactDetails();
    }
  }, [loading, prePopulateContactDetails]);

  if (
    !loading &&
    (error ||
      !addressId ||
      // @ts-expect-error -- variable is possibly undefined/null
      !propertyTags.includes(ListingTag.BUY_DIRECT_ELIGIBLE))
  ) {
    return <NotFoundPage />;
  }

  if (loading) {
    return <InterstitialLoader />;
  }

  if (!isOpendoorOwned) {
    navigationHelpers.hardNavAssign(`/dashboard/buy/offer?id=${addressId}`);
  }

  const shouldShowInfoPanel =
    state.value !== CheckoutState.CreateBuyDirectBackupOffer &&
    state.value !== CheckoutState.CreateBuyDirectListingOfferDeadline &&
    state.value !== CheckoutState.Redirect;

  let activeStep = <InterstitialLoader />;
  const previousState = state.history?.historyValue?.current as CheckoutState;
  switch (state.value) {
    case CheckoutState.ReviewDetails:
      if (showDuplicateOffersModal) {
        activeStep = <Box />;
      } else {
        activeStep = (
          <ReviewDetails buyDirectDiscount={buyDirectDiscount} listPrice={listPrice} send={send} />
        );
      }
      break;
    case CheckoutState.ContactDetails:
      activeStep = (
        <ContactDetails
          buyerDetails={state.context.contactDetails}
          hasOwnAgent={state.context.ownAgent}
          isContingentSale={state.context.contingentSale}
          send={send}
        />
      );
      break;
    case CheckoutState.FundingSource:
      activeStep = (
        <FundingSource
          closeDate={state.context?.closeDate}
          downPaymentAmount={state.context?.mortgageDownPaymentAmount}
          downPaymentPercentage={state.context?.mortgageDownPaymentPercentage}
          fundingSource={state.context.fundingSource}
          listPrice={listPrice}
          loanAmount={state.context?.loanAmount}
          send={send}
          fhaEligibilityDate={fhaEligibilityDate}
        />
      );
      break;
    case CheckoutState.PreApproval:
    case CheckoutState.PreApprovalConfirmation:
      activeStep = (
        <PreApproval checkoutState={state.value} uploadIdentifier={displaySlug} send={send} />
      );
      break;
    case CheckoutState.ReviewContractDetails:
    case CheckoutState.CreateBuyDirectListingOffer:
      activeStep = (
        <ContractDetails
          buyDirectDiscount={buyDirectDiscount}
          send={send}
          listPrice={listPrice}
          state={state}
        />
      );
      break;
    case CheckoutState.OwnAgent:
      activeStep = <OwnAgent />;
      break;
    case CheckoutState.BackupOfferDetails:
    case CheckoutState.OfferDeadline:
    case CheckoutState.InContract:
      activeStep = <BackupOffer buyerDetails={state.context.contactDetails} send={send} />;
      break;
    case CheckoutState.Confirmation:
      activeStep = <Confirmation addressId={addressId} previousState={previousState} />;
      break;
    case CheckoutState.ContingentSale:
      activeStep = <ContingentSaleStep addressId={addressId} />;
      break;
    case CheckoutState.Redirect:
      activeStep = <InterstitialLoader />;
      if (
        previousState === CheckoutState.CreateBuyDirectListingOffer &&
        state.context.listingOfferId
      ) {
        handleCheckoutCompletionRedirect();
      }
      // If there is an error and a listing offer wasn't created, fall back to ContractDetails screen
      if (showErrorModal) {
        activeStep = (
          <ContractDetails
            buyDirectDiscount={buyDirectDiscount}
            send={send}
            listPrice={listPrice}
            state={state}
          />
        );
      }
      break;
    case CheckoutState.CreateBuyDirectBackupOffer:
    case CheckoutState.CreateBuyDirectListingOfferDeadline:
      activeStep = <InterstitialLoader />;
      break;
    default:
      activeStep = <Flex />;
      break;
  }

  return (
    <ThemedLite theme="novo">
      <Flex flexDirection={['column', null, 'row']} py={8}>
        <Flex pt={[4, null, 8]} mx={[0, null, 8]} width={['100%', '100%', '30%', '40%']}>
          {shouldShowInfoPanel && (
            <InfoPanel
              bathrooms={bathrooms}
              bedrooms={bedrooms}
              buyDirectDiscount={buyDirectDiscount}
              city={city}
              currentState={state.value}
              listPrice={listPrice}
              photo={photo}
              postalCode={postalCode}
              previousState={previousState}
              sellerDisclosuresDownloadUrl={sellerDisclosuresDownloadUrl}
              state={stateAbbrv}
              street={street}
              sqft={sqft}
            />
          )}
        </Flex>
        <Flex
          py={[4, null, 8]}
          mx={[0, 0, 10, 8]}
          width={['100%', '100%', '70%', '60%']}
          px={[6, 8, 0, 0]}
        >
          {activeStep}
        </Flex>
      </Flex>
      <Modal
        aria-label="Duplicate offer"
        analyticsName="cake-buy-direct-create-listing-offer-duplicate-offer-modal"
        hasPadding
        isOpen={showDuplicateOffersModal}
        onRequestClose={closeDuplicateOffersModal}
        showClose={false}
        width="350px"
      >
        <Flex flexDirection="column" height="100%" alignItems="flex-start">
          <Text size="150" fontWeight="medium">
            You started a contract on this home
          </Text>
          <Text size="110" mt={6}>
            Let’s get you back to the original contract so you can finish signing it.
          </Text>
          <Button
            analyticsName="cake-buy-direct-close-duplicate-offer-modal"
            aria-label="Return to in-progress contract"
            variant="secondary"
            size="md"
            onClick={closeDuplicateOffersModal}
            mx={6}
            mt={8}
            alignSelf="center"
          >
            Finish signing contract
          </Button>
        </Flex>
      </Modal>
      <Modal
        aria-label="Call us to create your contract"
        analyticsName="cake-buy-direct-create-listing-offer-error-modal"
        hasPadding
        isOpen={showErrorModal}
        onRequestClose={closeErrorModal}
        showClose
        width="350px"
      >
        <Flex flexDirection="column" height="100%" alignItems="flex-start">
          <Text size="150" fontWeight="medium" mt={-6}>
            Call us to create your contract
          </Text>
          <Text size="110" mt={6}>
            There was a technical issue (don’t worry, it’s on our end). To create your contract,
            call the Customer Care team at (512) 706-9938.
          </Text>
          <Button
            analyticsName="cake-buy-direct-close-error-modal"
            aria-label="Ok"
            variant="secondary"
            size="md"
            onClick={closeErrorModal}
            mx={6}
            mt={8}
            alignSelf="center"
          >
            Ok
          </Button>
        </Flex>
      </Modal>
    </ThemedLite>
  );
};
